// @tennis-matches/services/winterdoppel/groups

import { getSupabaseClient } from "../supabase/index.js";
import {
  isoWeekday,
  parseIsoDate,
  toIsoDate,
  generateSessionDates,
} from "./dateUtils.js";
import {
  isYmdWithinSchoolHolidays,
  expandSchoolHolidayRangesForWeekendSports,
  schoolHolidayNameForYmd,
} from "../school-holidays/school-holidays-display.js";
import { fetchSchoolHolidaysForYears } from "../school-holidays/index.js";
import {
  applyWinterDoppelDateOverrides,
  loadWinterDoppelDateOverrides,
} from "./dateOverrides.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

const GROUP_SELECT =
  "id,tenant_id,created_by,series_id,name,kind,interval_weeks,weekday,start_time,end_time,schedule_start,schedule_end,holiday_subdivision,exclude_holidays,notes,trainer_id,participant_hourly_rate,created_at,updated_at";

const MEMBER_SELECT = "id,tenant_id,created_by,group_id,player_id,created_at";

/**
 * Termine einer festen Gruppe: erster Treffer Wochentag ab schedule_start, dann +interval Wochen.
 * @param {object} group
 * @param {{ holidays?: Array }} [opts]
 * @returns {string[]}
 */
export function generateWinterDoppelGroupDates(group = {}, opts = {}) {
  const weekday = Number(group.weekday ?? group.weekDay);
  const interval = Math.max(
    1,
    Math.min(8, Number(group.interval_weeks ?? group.intervalWeeks) || 1),
  );
  const start = parseIsoDate(group.schedule_start || group.scheduleStart);
  const end = parseIsoDate(
    group.schedule_end ||
      group.scheduleEnd ||
      group.season_end ||
      group.seasonEnd,
  );
  if (!start || !end || !(weekday >= 1 && weekday <= 7) || start > end) {
    return [];
  }

  const first = new Date(start);
  while (isoWeekday(first) !== weekday) {
    first.setDate(first.getDate() + 1);
    if (first > end) return [];
  }

  const skipHolidays =
    group.exclude_holidays !== false &&
    Array.isArray(opts.holidays) &&
    opts.holidays.length > 0;
  const out = [];
  const cur = new Date(first);
  while (cur <= end) {
    const iso = toIsoDate(cur);
    if (!(skipHolidays && isYmdWithinSchoolHolidays(iso, opts.holidays))) {
      out.push(iso);
    }
    cur.setDate(cur.getDate() + interval * 7);
  }
  return out;
}

export async function loadWinterDoppelGroupDatesWithHolidays(group) {
  const subdivision = __asStr(
    group?.holiday_subdivision || group?.holidaySubdivision,
  );
  let holidays = [];
  if (group?.exclude_holidays !== false && subdivision) {
    const start = String(group.schedule_start || "").slice(0, 4);
    const end = String(group.schedule_end || group.schedule_start || "").slice(
      0,
      4,
    );
    const y0 = Number(start) || new Date().getFullYear();
    const y1 = Number(end) || y0;
    try {
      const res = await fetchSchoolHolidaysForYears({
        subdivisionCode: subdivision,
        yearFrom: Math.min(y0, y1),
        yearTo: Math.max(y0, y1),
      });
      if (res?.ok) {
        holidays = expandSchoolHolidayRangesForWeekendSports(
          res.holidays || [],
        );
      }
    } catch (_) {
      holidays = [];
    }
  }
  return {
    dates: generateWinterDoppelGroupDates(group, { holidays }),
    holidays,
  };
}

const IN_QUERY_CHUNK = 80;

function __attachGroupMembers(list, members) {
  const byGroup = new Map();
  for (const m of members || []) {
    const gid = String(m.group_id);
    const arr = byGroup.get(gid) || [];
    arr.push(m);
    byGroup.set(gid, arr);
  }
  return (list || []).map((g) => ({
    ...g,
    members: byGroup.get(String(g.id)) || [],
    playerIds: (byGroup.get(String(g.id)) || []).map((m) => m.player_id),
  }));
}

export async function loadWinterDoppelPlayerGroups(seriesId) {
  const sid = __asStr(seriesId);
  if (!sid) return [];
  const supabase = await getSupabaseClient();
  const { data: groups, error } = await supabase
    .from("winterdoppel_player_groups")
    .select(GROUP_SELECT)
    .eq("series_id", sid)
    .order("created_at", { ascending: true });
  if (error) throw error;
  const list = Array.isArray(groups) ? groups : [];
  if (!list.length) return [];

  const { data: members, error: memErr } = await supabase
    .from("winterdoppel_player_group_members")
    .select(MEMBER_SELECT)
    .in(
      "group_id",
      list.map((g) => g.id),
    );
  if (memErr) throw memErr;
  return __attachGroupMembers(list, members);
}

/** Gruppen (+Members) für viele Serien in wenigen Roundtrips. */
export async function loadWinterDoppelPlayerGroupsForSeriesIds(seriesIds = []) {
  const ids = [
    ...new Set((seriesIds || []).map((id) => __asStr(id)).filter(Boolean)),
  ];
  if (!ids.length) return [];
  const supabase = await getSupabaseClient();
  const groupParts = [];
  for (let i = 0; i < ids.length; i += IN_QUERY_CHUNK) {
    const chunk = ids.slice(i, i + IN_QUERY_CHUNK);
    groupParts.push(
      supabase
        .from("winterdoppel_player_groups")
        .select(GROUP_SELECT)
        .in("series_id", chunk)
        .order("created_at", { ascending: true }),
    );
  }
  const groupResults = await Promise.all(groupParts);
  const list = [];
  for (const { data, error } of groupResults) {
    if (error) throw error;
    if (Array.isArray(data)) list.push(...data);
  }
  if (!list.length) return [];

  const groupIds = list.map((g) => g.id).filter(Boolean);
  const memberParts = [];
  for (let i = 0; i < groupIds.length; i += IN_QUERY_CHUNK) {
    const chunk = groupIds.slice(i, i + IN_QUERY_CHUNK);
    memberParts.push(
      supabase
        .from("winterdoppel_player_group_members")
        .select(MEMBER_SELECT)
        .in("group_id", chunk),
    );
  }
  const memberResults = await Promise.all(memberParts);
  const members = [];
  for (const { data, error } of memberResults) {
    if (error) throw error;
    if (Array.isArray(data)) members.push(...data);
  }
  return __attachGroupMembers(list, members);
}

export async function upsertWinterDoppelPlayerGroup(input = {}) {
  const sid = __asStr(input.series_id || input.seriesId);
  const name = __asStr(input.name);
  const playerIds = [
    ...new Set(
      (Array.isArray(input.playerIds)
        ? input.playerIds
        : input.player_ids || []
      )
        .map(__asStr)
        .filter(Boolean),
    ),
  ];
  if (!sid || !name) throw new Error("winterdoppel.group.missingFields");
  if (playerIds.length < 2) throw new Error("winterdoppel.group.needMembers");

  const weekday = Number(input.weekday ?? input.weekDay);
  const intervalWeeks = Math.max(
    1,
    Math.min(8, Number(input.interval_weeks ?? input.intervalWeeks) || 1),
  );
  const scheduleStart = __asStr(input.schedule_start || input.scheduleStart);
  if (!(weekday >= 1 && weekday <= 7) || !scheduleStart) {
    throw new Error("winterdoppel.group.invalidSchedule");
  }

  const row = {
    series_id: sid,
    name,
    kind: "fixed_training",
    interval_weeks: intervalWeeks,
    weekday,
    start_time: __asStr(input.start_time || input.startTime) || null,
    end_time: __asStr(input.end_time || input.endTime) || null,
    schedule_start: scheduleStart,
    schedule_end: __asStr(input.schedule_end || input.scheduleEnd) || null,
    holiday_subdivision:
      __asStr(input.holiday_subdivision || input.holidaySubdivision) || null,
    exclude_holidays:
      input.exclude_holidays !== false && input.excludeHolidays !== false,
    notes: __asStr(input.notes) || null,
    trainer_id: __asStr(input.trainer_id || input.trainerId) || null,
  };
  if (
    input.participant_hourly_rate !== undefined ||
    input.participantHourlyRate !== undefined
  ) {
    const rate = Number(
      input.participant_hourly_rate ?? input.participantHourlyRate,
    );
    row.participant_hourly_rate =
      Number.isFinite(rate) && rate > 0 ? Math.round(rate * 100) / 100 : null;
  }
  const tid = __asStr(input.tenant_id || input.tenantId);
  if (tid) row.tenant_id = tid;

  const supabase = await getSupabaseClient();
  const groupId = __asStr(input.id || input.groupId);
  let saved;
  if (groupId) {
    const { data, error } = await supabase
      .from("winterdoppel_player_groups")
      .update(row)
      .eq("id", groupId)
      .select(GROUP_SELECT)
      .single();
    if (error) throw error;
    saved = data;
    const { error: delErr } = await supabase
      .from("winterdoppel_player_group_members")
      .delete()
      .eq("group_id", groupId);
    if (delErr) throw delErr;
  } else {
    const { data, error } = await supabase
      .from("winterdoppel_player_groups")
      .insert(row)
      .select(GROUP_SELECT)
      .single();
    if (error) throw error;
    saved = data;
  }

  const memberRows = playerIds.map((player_id) => {
    const m = { group_id: saved.id, player_id };
    if (tid) m.tenant_id = tid;
    return m;
  });
  const { error: memErr } = await supabase
    .from("winterdoppel_player_group_members")
    .insert(memberRows);
  if (memErr) throw memErr;

  return loadWinterDoppelPlayerGroups(sid).then((all) =>
    all.find((g) => g.id === saved.id),
  );
}

export async function deleteWinterDoppelPlayerGroup(groupId) {
  const supabase = await getSupabaseClient();
  const { error } = await supabase
    .from("winterdoppel_player_groups")
    .delete()
    .eq("id", __asStr(groupId));
  if (error) throw error;
}

/** Mitgliederlisten für Planungs-Zwang (mind. 2 IDs). */
export function playerGroupsToForcedIds(groups = []) {
  return (groups || [])
    .map((g) => [
      ...new Set(
        (Array.isArray(g?.playerIds)
          ? g.playerIds
          : (g?.members || []).map((m) => m.player_id)
        )
          .map((id) => String(id || "").trim())
          .filter(Boolean),
      ),
    ])
    .filter((ids) => ids.length >= 2);
}

/**
 * Nächstes Startdatum +days, ISO YYYY-MM-DD.
 * @param {string} iso
 * @param {number} days
 */
export function shiftWinterDoppelIsoDate(iso, days) {
  const d = parseIsoDate(iso);
  if (!d) return "";
  d.setDate(d.getDate() + Number(days || 0));
  return toIsoDate(d);
}

/**
 * Vorschlag für neue Gruppe: gleicher Wochentag/Intervall → +7 Tage versetzen,
 * damit bei 1 Platz beide Gruppen eigene Termine bekommen (~12 statt Wechsel).
 */
export function suggestWinterDoppelGroupScheduleStart({
  existingGroups = [],
  weekday,
  intervalWeeks,
  preferredStart,
  seasonEnd,
} = {}) {
  const preferred = String(preferredStart || "").slice(0, 10);
  const wd = Number(weekday);
  const interval = Math.max(1, Number(intervalWeeks) || 1);
  if (!preferred || !(wd >= 1 && wd <= 7)) return preferred;

  const peers = (existingGroups || []).filter((g) => {
    const gWd = Number(g.weekday ?? g.weekDay);
    const gIv = Math.max(1, Number(g.interval_weeks ?? g.intervalWeeks) || 1);
    return gWd === wd && gIv === interval;
  });
  if (!peers.length) return preferred;

  const occupied = new Set();
  for (const g of peers) {
    for (const d of generateWinterDoppelGroupDates({
      weekday: wd,
      interval_weeks: interval,
      schedule_start: g.schedule_start || g.scheduleStart,
      schedule_end: g.schedule_end || g.scheduleEnd || seasonEnd,
      exclude_holidays: false,
    })) {
      occupied.add(d);
    }
  }

  let candidate = preferred;
  for (let i = 0; i < 14; i += 1) {
    const dates = generateWinterDoppelGroupDates({
      weekday: wd,
      interval_weeks: interval,
      schedule_start: candidate,
      schedule_end: seasonEnd || candidate,
      exclude_holidays: false,
    });
    const first = dates[0];
    if (first && !occupied.has(first)) return candidate;
    candidate = shiftWinterDoppelIsoDate(candidate, 7);
    if (!candidate) break;
  }
  return preferred;
}

/**
 * Pro Gruppe Termine (Ferien optional bereits in group.dates).
 * @returns {Array<{ group: object, playerIds: string[], dates: Set<string> }>}
 */
export function buildWinterDoppelGroupScheduleIndex(groups = []) {
  return (groups || [])
    .map((g) => {
      const playerIds = [
        ...new Set(
          (Array.isArray(g?.playerIds)
            ? g.playerIds
            : (g?.members || []).map((m) => m.player_id)
          )
            .map((id) => String(id || "").trim())
            .filter(Boolean),
        ),
      ];
      const dates = new Set(
        (Array.isArray(g?.dates)
          ? g.dates
          : generateWinterDoppelGroupDates(g)
        ).map(String),
      );
      return { group: g, playerIds, dates };
    })
    .filter((x) => x.playerIds.length >= 2 && x.dates.size > 0);
}

/** Spieler → erlaubte Termine (Union aller Gruppen). */
export function buildWinterDoppelPlayerDateIndex(groupSchedules = []) {
  /** @type {Map<string, Set<string>>} */
  const byPlayer = new Map();
  for (const gs of groupSchedules || []) {
    for (const pid of gs.playerIds) {
      const set = byPlayer.get(pid) || new Set();
      for (const d of gs.dates) set.add(d);
      byPlayer.set(pid, set);
    }
  }
  return byPlayer;
}

/**
 * Bei gleichem Wochentag + Intervall ≥ 2: überlappende Gruppen phasenweise versetzen
 * (Gruppe 1 Woche A, Gruppe 2 Woche B), damit das wöchentliche Abo abwechselnd belegt wird.
 * @param {object[]} groups
 * @returns {object[]}
 */
export function withWinterDoppelAlternatingGroupPhases(groups = []) {
  const list = (Array.isArray(groups) ? groups : []).map((g) => ({ ...g }));
  /** @type {Map<string, object[]>} */
  const buckets = new Map();
  for (const g of list) {
    const wd = Number(g.weekday ?? g.weekDay);
    const iv = Math.max(1, Number(g.interval_weeks ?? g.intervalWeeks) || 1);
    if (!(wd >= 1 && wd <= 7) || iv < 2) continue;
    const key = `${wd}|${iv}`;
    const arr = buckets.get(key) || [];
    arr.push(g);
    buckets.set(key, arr);
  }
  for (const arr of buckets.values()) {
    arr.sort(
      (a, b) =>
        String(a.created_at || a.createdAt || "").localeCompare(
          String(b.created_at || b.createdAt || ""),
        ) || String(a.id || "").localeCompare(String(b.id || "")),
    );
    if (arr.length < 2) continue;
    const wd = Number(arr[0].weekday ?? arr[0].weekDay);
    const iv = Math.max(
      1,
      Number(arr[0].interval_weeks ?? arr[0].intervalWeeks) || 1,
    );
    const seasonEnd =
      arr[0].schedule_end ||
      arr[0].scheduleEnd ||
      arr[0].season_end ||
      arr[0].seasonEnd ||
      null;
    for (let i = 1; i < arr.length; i += 1) {
      const earlier = arr.slice(0, i);
      const preferred =
        arr[i].schedule_start ||
        arr[i].scheduleStart ||
        earlier[0].schedule_start;
      const next = suggestWinterDoppelGroupScheduleStart({
        existingGroups: earlier,
        weekday: wd,
        intervalWeeks: iv,
        preferredStart: preferred,
        seasonEnd,
      });
      if (next) {
        arr[i].schedule_start = next;
        arr[i].scheduleStart = next;
      }
    }
  }
  return list;
}

/**
 * Tauscht die effektiven 2-Wochen-Phasen paarweise (1↔2) je Wochentag-Bucket.
 * Persistierbare schedule_start-Werte, damit die Grobplanung den Tausch behält.
 * @param {object[]} groups
 * @returns {object[]}
 */
export function swapWinterDoppelAlternatingGroupPhaseStarts(groups = []) {
  const list = (Array.isArray(groups) ? groups : []).map((g) => ({ ...g }));
  const phasedById = new Map(
    withWinterDoppelAlternatingGroupPhases(list).map((g) => [
      String(g.id || ""),
      g,
    ]),
  );

  /** @type {Map<string, object[]>} */
  const buckets = new Map();
  for (const g of list) {
    const wd = Number(g.weekday ?? g.weekDay);
    const iv = Math.max(1, Number(g.interval_weeks ?? g.intervalWeeks) || 1);
    if (!(wd >= 1 && wd <= 7) || iv < 2 || !g?.id) continue;
    const key = `${wd}|${iv}`;
    const arr = buckets.get(key) || [];
    arr.push(g);
    buckets.set(key, arr);
  }

  for (const arr of buckets.values()) {
    arr.sort(
      (a, b) =>
        String(a.created_at || a.createdAt || "").localeCompare(
          String(b.created_at || b.createdAt || ""),
        ) || String(a.id || "").localeCompare(String(b.id || "")),
    );
    if (arr.length < 2) continue;
    for (let i = 0; i + 1 < arr.length; i += 2) {
      const a = arr[i];
      const b = arr[i + 1];
      const startA = String(
        phasedById.get(String(a.id))?.schedule_start || a.schedule_start || "",
      ).slice(0, 10);
      const startB = String(
        phasedById.get(String(b.id))?.schedule_start || b.schedule_start || "",
      ).slice(0, 10);
      if (!startA || !startB || startA === startB) continue;
      a.schedule_start = startB;
      a.scheduleStart = startB;
      b.schedule_start = startA;
      b.scheduleStart = startA;
    }
  }
  return list;
}

/**
 * Ob es mindestens ein Paar 2-Wochen-Gruppen zum Phasentausch gibt.
 * @param {object[]} groups
 */
export function canSwapWinterDoppelGroupPhases(groups = []) {
  /** @type {Map<string, number>} */
  const counts = new Map();
  for (const g of groups || []) {
    const wd = Number(g.weekday ?? g.weekDay);
    const iv = Math.max(1, Number(g.interval_weeks ?? g.intervalWeeks) || 1);
    if (!(wd >= 1 && wd <= 7) || iv < 2) continue;
    const key = `${wd}|${iv}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.values()].some((n) => n >= 2);
}

/**
 * Persistiert den Tausch der Wochenphasen und liefert die Gruppen neu.
 * @param {string} seriesId
 */
export async function swapWinterDoppelSeriesGroupPhases(seriesId) {
  const sid = __asStr(seriesId);
  if (!sid) throw new Error("winterdoppel.group.missingFields");
  const groups = await loadWinterDoppelPlayerGroups(sid);
  if (!canSwapWinterDoppelGroupPhases(groups)) {
    throw new Error("winterdoppel.group.swapPhasesUnavailable");
  }
  const swapped = swapWinterDoppelAlternatingGroupPhaseStarts(groups);
  const supabase = await getSupabaseClient();
  for (const g of swapped) {
    const prev = groups.find((x) => String(x.id) === String(g.id));
    const nextStart = String(g.schedule_start || "").slice(0, 10);
    const prevStart = String(prev?.schedule_start || "").slice(0, 10);
    if (!g.id || !nextStart || nextStart === prevStart) continue;
    const { error } = await supabase
      .from("winterdoppel_player_groups")
      .update({ schedule_start: nextStart })
      .eq("id", g.id);
    if (error) throw error;
  }
  return loadWinterDoppelPlayerGroups(sid);
}

/**
 * Termine einer Trainingsserie = Gruppen-Termine (schedule_start + Intervall),
 * sonst wöchentliche Abo-Termine der Serie, abzüglich Schulferien/Feiertage,
 * plus manuelle Overrides.
 * @returns {Promise<string[]>}
 */
export async function resolveWinterDoppelTrainingSessionDates(
  series,
  groups = [],
  overrides = null,
) {
  const weekly = generateSessionDates({
    seasonStart: series?.season_start,
    seasonEnd: series?.season_end,
    weekdays: series?.weekdays,
  });
  const list = Array.isArray(groups) ? groups : [];
  const seasonEnd = series?.season_end || series?.seasonEnd;
  const phased = withWinterDoppelAlternatingGroupPhases(
    list.map((g) => ({
      ...g,
      schedule_end: g.schedule_end || g.scheduleEnd || seasonEnd,
      season_end: seasonEnd,
    })),
  );
  const fromGroups = new Set();
  for (const g of phased) {
    for (const d of generateWinterDoppelGroupDates({
      ...g,
      exclude_holidays: false,
    })) {
      fromGroups.add(d);
    }
  }
  let dates = fromGroups.size ? [...fromGroups].sort() : [...weekly];
  // Explizit gesetzter Gruppenstart (erster Treffer ab schedule_start) bleibt,
  // auch wenn er im erweiterten Ferien-Wochenende liegt (z. B. Start am 31.10.).
  const firstGroupDates = new Set();
  for (const g of phased) {
    const first = generateWinterDoppelGroupDates({
      ...g,
      exclude_holidays: false,
    })[0];
    if (first) firstGroupDates.add(first);
  }
  const subdivision =
    __asStr(series?.holiday_subdivision || series?.holidaySubdivision) ||
    __asStr(list[0]?.holiday_subdivision || list[0]?.holidaySubdivision);
  const excludeHolidays =
    list.length === 0
      ? Boolean(subdivision)
      : list.some((g) => g.exclude_holidays !== false);

  if (excludeHolidays && subdivision && dates.length) {
    const y0 = Number(String(dates[0]).slice(0, 4));
    const y1 = Number(String(dates[dates.length - 1]).slice(0, 4));
    try {
      const res = await fetchSchoolHolidaysForYears({
        subdivisionCode: subdivision,
        yearFrom: y0,
        yearTo: y1,
      });
      if (res?.ok && Array.isArray(res.holidays) && res.holidays.length) {
        const holidays = expandSchoolHolidayRangesForWeekendSports(
          res.holidays,
        );
        dates = dates.filter(
          (d) =>
            firstGroupDates.has(d) || !isYmdWithinSchoolHolidays(d, holidays),
        );
      }
    } catch (_) {
      /* Termine ohne Ferienfilter behalten */
    }
  }

  const ov =
    overrides ??
    (series?.id ? await loadWinterDoppelDateOverrides(series.id) : []);
  return applyWinterDoppelDateOverrides(dates, ov, weekly);
}

/**
 * Serien-Wochentage ohne Training: Ferien (ohne force_on) + manuell aus (force_off).
 * @param {object} series
 * @param {{ overrides?: Array<{ session_date: string, kind: string }> }} [opts]
 * @returns {Promise<Array<{ date: string, name: string, source: 'holiday'|'manual_off' }>>}
 */
export async function loadWinterDoppelSeriesHolidayMarkers(series, opts = {}) {
  const weekly = generateSessionDates({
    seasonStart: series?.season_start,
    seasonEnd: series?.season_end,
    weekdays: series?.weekdays,
  });
  const subdivision = __asStr(
    series?.holiday_subdivision || series?.holidaySubdivision,
  );
  let overrides = Array.isArray(opts?.overrides) ? opts.overrides : null;
  if (!overrides) {
    try {
      overrides = series?.id
        ? await loadWinterDoppelDateOverrides(series.id)
        : [];
    } catch (_) {
      // z. B. Public-Schedule ohne RLS-Zugriff auf Overrides
      overrides = [];
    }
  }
  const forceOn = new Set(
    (overrides || [])
      .filter((o) => o.kind === "force_on")
      .map((o) => String(o.session_date || "").slice(0, 10))
      .filter(Boolean),
  );
  const forceOff = new Set(
    (overrides || [])
      .filter((o) => o.kind === "force_off")
      .map((o) => String(o.session_date || "").slice(0, 10))
      .filter(Boolean),
  );

  /** @type {Array<{ date: string, name: string, source: string }>} */
  const markers = [];

  if (subdivision && weekly.length) {
    const y0 = Number(String(weekly[0]).slice(0, 4));
    const y1 = Number(String(weekly[weekly.length - 1]).slice(0, 4));
    try {
      const res = await fetchSchoolHolidaysForYears({
        subdivisionCode: subdivision,
        yearFrom: y0,
        yearTo: y1,
      });
      if (res?.ok && res.holidays?.length) {
        const holidays = expandSchoolHolidayRangesForWeekendSports(
          res.holidays,
        );
        for (const date of weekly) {
          if (forceOn.has(date)) continue;
          if (!isYmdWithinSchoolHolidays(date, holidays)) continue;
          markers.push({
            date,
            name: schoolHolidayNameForYmd(date, holidays) || "",
            source: "holiday",
          });
        }
      }
    } catch (_) {
      /* ignore */
    }
  }

  for (const date of forceOff) {
    if (markers.some((m) => m.date === date)) continue;
    markers.push({
      date,
      name: "",
      source: "manual_off",
    });
  }

  return markers.sort((a, b) => a.date.localeCompare(b.date));
}

/** Ob ein Datum natürlicher Ferien-/Feiertag ist (ohne manuelle Overrides). */
export async function isWinterDoppelNaturalHolidayDate(series, sessionDate) {
  const date = String(sessionDate || "").slice(0, 10);
  const subdivision = __asStr(
    series?.holiday_subdivision || series?.holidaySubdivision,
  );
  if (!date || !subdivision) return false;
  const y = Number(date.slice(0, 4));
  try {
    const res = await fetchSchoolHolidaysForYears({
      subdivisionCode: subdivision,
      yearFrom: y,
      yearTo: y,
    });
    if (!res?.ok || !res.holidays?.length) return false;
    const holidays = expandSchoolHolidayRangesForWeekendSports(res.holidays);
    return isYmdWithinSchoolHolidays(date, holidays);
  } catch (_) {
    return false;
  }
}
